import { useEffect, useState, useCallback } from 'react';
import { ILocalization, IDesignerAppshellPayload, IPersonalizeOverlayPayload } from '../types';
import { PersonalizeOverlay } from './personalizeOverlay';
import { PersonalizeInfoOverlay } from './personalizeInfoOverlay';
import { LogoCreaterService } from '../logoCreaterService/logoCreaterService';
import { BasketService } from '../basketService/basketService';

interface IPersonalizeOverlyWrapperProps {
    localization: ILocalization;
    ssoId: string;
    isEsApp: boolean;
    isFullyRegistered: boolean;
    isBasketPage: boolean;
    hasCustomerLogo: boolean;
}

export const PersonalizeOverlayWrapper = (props: IPersonalizeOverlyWrapperProps) => {
    const afterLoginParam = 'openLogoCreator';
    const appShellSignature = 'ESPP.LogoCreator.PersonalizeOverlayWrapper';
    const lcService = LogoCreaterService.instance;
    const basketService = BasketService.instance;

    const [poIsVisible, setPoIsVisible] = useState<boolean>(false);
    const [designerPayload, setDesignerPayload] = useState<IDesignerAppshellPayload>(null);
    const [logoServiceInfoUrl, setlogoServiceInfoUrl] = useState<string | undefined>(undefined);
    const [isSmakeDesignerAvailable, setIsSmakeDesignerAvailable] = useState<boolean | undefined>(undefined);
    const [confirmRegistrationHintIsVisible, setConfirmRegistrationHintIsVisible] = useState<boolean>(false);
    const [basketItemGuid, setBasketItemGuid] = useState<string | undefined>(undefined);
    useEffect(() => {
        // handle after login case
        if (window) {
            const searchParams = new URL(window.location.href).searchParams;

            if (searchParams.has(afterLoginParam)) {
                // get payload from param
                const payloadString = searchParams.get(afterLoginParam);
                const payload = decodePayload(payloadString);

                //drop param;
                searchParams.delete(afterLoginParam);
                const paramsStr = searchParams.toString();
                const newUrl = window.location.protocol +
                    '//' +
                    window.location.host +
                    window.location.pathname +
                    (paramsStr ? '?' + paramsStr : '') +
                    window.location.hash;
                window.history.replaceState({}, 'unused', newUrl);

                if (payload) {
                    //open LogoCreator 
                    openLogoCreatorDesigner(payload);
                    // maybe LogoCreator is not loaded yet, make sure its opened when its ready
                    window.shell.once('ESPP.LogoCreator.InitializationFinished',
                        () => {
                            openLogoCreatorDesigner(payload);
                        });
                }
            }
        }
    }, []);

    const handleOpen = useCallback(async (payload: IPersonalizeOverlayPayload) => {
        let smakeAvailable = payload.isSmakeDesignerAvailable;
        if (smakeAvailable === undefined) {
            const masterArticleNo = payload.designerPayload.masterArticleNumber;
            const savKey = payload.designerPayload.preselectedArticles[0].variant.external_identifier;
            smakeAvailable = await lcService.getArticleState(masterArticleNo, savKey).catch(() => false);
        }

        if (smakeAvailable) {
            setlogoServiceInfoUrl(payload.logoServiceInfoUrl);
            setDesignerPayload(payload.designerPayload);
            setIsSmakeDesignerAvailable(smakeAvailable);
            setBasketItemGuid(payload.basketItemGuid);
            setPoIsVisible(true);
        } else {
            if (payload.basketItemGuid) {
                basketService.callSimplePersonalize(payload.basketItemGuid);
            } else if (payload.logoServiceInfoUrl) {
                window.open(payload.logoServiceInfoUrl, '_self');
            } else {
                console.warn('LC: lack of info');
            }
        }
    }, []);

    const handleTryOpen = useCallback((payload: IDesignerAppshellPayload) => {
        tryOpenDesigner(payload);
    }, []);

    useEffect(() => {
        if (window) {
            // open personalized overlay
            window.shell.subscribeTo('ESPP.LogoCreator.OpenPersonalizeOverlay',handleOpen, appShellSignature);
            // try to directly open LC
            window.shell.subscribeTo('ESPP.LogoCreator.TryToOpenDesignerWindow', handleTryOpen, appShellSignature);
            // cover old event call with LC
            window.shell.subscribeTo('ESPP.LogoCreator.OpenDesignerWindow', handleTryOpen, appShellSignature);
            // close Po if simple personalization modal opened
            window.shell.subscribeTo('ESSA.BasketPage.SimplePersonalizationModalOpened', () => {
                if (poIsVisible) {
                    setPoIsVisible(false);
                }
            }, appShellSignature);
        }
        return () => {
            window.shell.unsubscribeFrom('ESPP.LogoCreator.OpenPersonalizeOverlay', appShellSignature);
            window.shell.unsubscribeFrom('ESPP.LogoCreator.TryToOpenDesignerWindow', appShellSignature);
            window.shell.unsubscribeFrom('ESSA.BasketPage.SimplePersonalizationModalOpened', appShellSignature);
        };
    }, [handleOpen, handleTryOpen, poIsVisible]);

    function closeOverlay(): void {
        setPoIsVisible(false);
        if (window) {
            window.shell.publishTo('ESPP.LogoCreator.PersonalizeOverlayClosed', {
                basketItemGuid: basketItemGuid,
                savKey: designerPayload?.preselectedArticles[0].variant.external_identifier,
                masterArticleNo: designerPayload?.masterArticleNumber
            });
        }
    }

    function closeConfirmHint(): void {
        setConfirmRegistrationHintIsVisible(false);
    }

    function tryOpenDesigner(payload: IDesignerAppshellPayload) {
        if (props.ssoId && props.ssoId.length > 0) {
            //if user is logged in
            //close this PO modal
            setPoIsVisible(false);
            openLogoCreatorDesigner(payload);
        } else {
            // user not logged in yet
            //2. after AMS is done switching, open it 
            window.shell.once('ESCRM.AccountMenu.AccountMenuSwitchedToLoginRequestedMode', () => {
                //this opens the flyout
                window.shell.publishTo('ESMKT.HeadR.ToggleFlyout', 'LoginFlyout');
                //close this PO modal
                setPoIsVisible(false);
            });

            // 1. request AMS to switch its text to a message defined by us
            const currentPageUrl = new URL(window.location.href);

            // only set afterLoginParam for none baksetPage case (PDP)
            if (!props.isBasketPage) {
                const payload64 = encodePayload(payload);
                currentPageUrl.searchParams.set(afterLoginParam, payload64);
            }

            // ESPP-11921 read current category from meta to make sure correct redirect behaivor
            const categoryValue: HTMLMetaElement | null = document.querySelector('meta[name="navkeypath"]');
            if (categoryValue) {
                currentPageUrl.searchParams.set('category', categoryValue.content);
            }

            const redirectUrl = `${currentPageUrl.pathname}${currentPageUrl.search}`;

            if (props.isEsApp) {
                const urlWithParamEncoded = encodeURIComponent(redirectUrl);
                const param = '?loginRequired=true&redirectUrl=' + urlWithParamEncoded;

                const loginForEsAppWithRedirect = props.localization.esAppLoginPageUrl + param;

                window.location.href = loginForEsAppWithRedirect;
                return;
            }

            window.shell.publishTo('ESCRM.AccountMenu.ServiceRequestsLogin',
                {
                    redirectUrl: redirectUrl,
                    message: props.localization.personalizationLoginHint,
                });
        }
    }

    function openLogoCreatorDesigner(payload: IDesignerAppshellPayload) {
        if (!props.isFullyRegistered) {
            // open hint modal;
            setConfirmRegistrationHintIsVisible(true);
        } else {
            if (typeof window !== 'undefined' && window.shell)
                window.shell.publishTo('ESPP.LogoCreator.OpenDesignerWindowConfirm', payload);
        }
    }

    function encodePayload(payload: IDesignerAppshellPayload): string {
        const json = JSON.stringify(payload);
        const bytes = new TextEncoder().encode(json);

        const CHUNK_SIZE = 0x8000; // 32 KB - safely below engine arg limits
        let binary = '';
        for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
            const chunk = bytes.subarray(i, i + CHUNK_SIZE);
            binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
        }

        return btoa(binary);
    }

    function decodePayload(encoded: string): IDesignerAppshellPayload | null {
        try {
            const binary = atob(encoded);
            const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
            const json = new TextDecoder().decode(bytes);
            return JSON.parse(json);
        } catch (e) {
            console.warn('LC: Failed to decode payload');
            return null;
        }
    }

    return <>
        <PersonalizeOverlay
            poIsVisible={poIsVisible}
            localization={props.localization}
            logoServiceInfoUrl={logoServiceInfoUrl}
            handleDesignYourselfClick={() => tryOpenDesigner(designerPayload)}
            closeOverlay={closeOverlay}
            hasCustomerLogo={props.hasCustomerLogo}
            basketItemGuid={basketItemGuid}
        ></PersonalizeOverlay>
        <PersonalizeInfoOverlay
            localization={props.localization}
            confirmRegistrationHintIsVisible={confirmRegistrationHintIsVisible}
            closeOverlay={closeConfirmHint}
        ></PersonalizeInfoOverlay >
    </>
}
